import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toImageUrl } from "../../../config";
import { elakayAutoText } from "../dataElakay";
import UniversalMediaPicker from "../../mediaLibrary/UniversalMediaPicker";
import { createMediaAsset } from "../../auth/apiMediaLibrary";
import {
  activateQrDesign,
  createQrDesign,
  deleteQrDesign,
  duplicateQrDesign,
  listQrDesigns,
  qrDesignImageUrl,
  updateQrDesign,
  uploadMediaFile,
} from "../../auth/apiQrDesigns";
import {
  DEFAULT_SETTINGS,
  EXPORT_FORMATS,
  EXPORT_SIZES,
  QR_MODES,
  exportDesign,
  normalizeSettings,
  renderDesign,
  verifyCanvas,
} from "./qrDesignRender";
import { removeImageBackground } from "./removeImageBackground";
import "./qrDesigner.css";

const t = (key, fallback, values) => elakayAutoText(key, fallback, values);
const HISTORY_LIMIT = 60;
const PREVIEW_PX = 512;

const MODE_LABELS = {
  get classic() { return t("qrd_modeClassic", "Classic QR Code"); },
  get logo() { return t("qrd_modeLogo", "Logo QR Code"); },
  get artistic() { return t("qrd_modeArtistic", "Artistic QR Code"); },
};

const isHttpUrl = (value) => {
  try {
    const url = new URL(String(value || ""));
    return (url.protocol === "http:" || url.protocol === "https:") && !url.username && !url.password;
  } catch {
    return false;
  }
};

const isSafeRemoteImageUrl = (value) => {
  const raw = String(value || "");
  if (raw.startsWith("/") && !raw.startsWith("//")) return true;
  if (/^data:image\/(png|jpeg|webp);base64,/i.test(raw)) return true;
  try {
    const { protocol, hostname, username, password } = new URL(raw);
    if (!["http:", "https:"].includes(protocol) || username || password) return false;
    const host = hostname.replace(/^\[|\]$/g, "").toLowerCase();
    if (host === "localhost" || host.endsWith(".localhost") || host.endsWith(".local") || host === "::" || host === "::1" || /^(fc|fd|fe[89ab])/i.test(host)) return false;
    const octets = host.split(".").map(Number);
    if (octets.length === 4 && octets.every((part) => Number.isInteger(part) && part >= 0 && part <= 255)) {
      const [a, b] = octets;
      if (a === 0 || a === 10 || a === 127 || (a === 169 && b === 254) || (a === 172 && b >= 16 && b <= 31) || (a === 192 && b === 168) || (a === 100 && b >= 64 && b <= 127) || a >= 224) return false;
    }
    return true;
  } catch {
    return false;
  }
};

const emptyDraft = (link = "") => ({
  id: null,
  name: "",
  mode: "classic",
  link,
  promoId: null,
  settings: { ...DEFAULT_SETTINGS },
  background: {},
  photo: {},
  logo: {},
});

// Images are drawn on a canvas (export, verification): load them with CORS.
const loadImage = (url) =>
  new Promise((resolve, reject) => {
    if (!url) {
      resolve(null);
      return;
    }
    const img = new Image();
    if (!String(url).startsWith("data:")) img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("IMAGE_LOAD_FAILED"));
    img.src = String(url).startsWith("data:") ? url : toImageUrl(url);
  });

const useLoadedImage = (url) => {
  const [state, setState] = useState({ img: null, error: "" });
  useEffect(() => {
    let active = true;
    if (!url) {
      setState({ img: null, error: "" });
      return undefined;
    }
    loadImage(url)
      .then((img) => { if (active) setState({ img, error: "" }); })
      .catch(() => { if (active) setState({ img: null, error: "IMAGE_LOAD_FAILED" }); });
    return () => { active = false; };
  }, [url]);
  return state;
};

const canvasBlob = (canvas) => new Promise((resolve) => canvas.toBlob(resolve, "image/png"));

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
};

const Slider = ({ label, value, min, max, step = 1, onChange, suffix = "" }) => (
  <label className="qrd-field qrd-slider">
    <span>{label} <em>{Math.round(value * 100) / 100}{suffix}</em></span>
    <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} />
  </label>
);

const ColorField = ({ label, value, onChange }) => (
  <label className="qrd-field qrd-color">
    <span>{label}</span>
    <span className="qrd-color-row">
      <input type="color" value={value} onChange={(e) => onChange(e.target.value)} />
      <input type="text" value={value} maxLength={7} onChange={(e) => /^#[0-9a-f]{0,6}$/i.test(e.target.value) && onChange(e.target.value)} />
    </span>
  </label>
);

/**
 * QR Code Designer (Admin Associate > Elakay Promo > "View QR Code &
 * Designer"). promoLinks: [{ id, label, value }] -- the user's promotion
 * links; any other http(s) link can be typed.
 */
export default function QrCodeDesigner({ promoLinks = [] }) {
  const [designs, setDesigns] = useState([]);
  const [listState, setListState] = useState({ loading: true, error: "" });
  const [draft, setDraft] = useState(() => emptyDraft(promoLinks[0]?.value || ""));
  const [history, setHistory] = useState({ past: [], future: [] });
  const lastPushRef = useRef(0);
  const [verification, setVerification] = useState({ ok: false, decoded: null, error: "PENDING" });
  const [busy, setBusy] = useState("");
  const [message, setMessage] = useState({ type: "", text: "" });
  const [pickerFor, setPickerFor] = useState(null); // "photo" | "logo"
  const [exportFormat, setExportFormat] = useState("png");
  const [exportSize, setExportSize] = useState(1024);
  const [previewDesign, setPreviewDesign] = useState(null);
  const [search, setSearch] = useState("");
  const [modeFilter, setModeFilter] = useState("all");
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!previewDesign) return undefined;
    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = (event) => { if (event.key === "Escape") setPreviewDesign(null); };
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [previewDesign]);

  const background = useLoadedImage(draft.background?.url || "");
  const photo = useLoadedImage(draft.mode === "artistic" ? draft.photo?.url : "");
  const logo = useLoadedImage(draft.mode === "logo" ? draft.logo?.url : "");
  const settings = useMemo(() => normalizeSettings(draft.settings), [draft.settings]);
  const visibleDesigns = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase();
    return designs.filter((design) => (modeFilter === "all" || design.mode === modeFilter) &&
      (!needle || [design.name, design.link, MODE_LABELS[design.mode]].some((value) => String(value || "").toLocaleLowerCase().includes(needle))));
  }, [designs, modeFilter, search]);

  const refresh = useCallback(async () => {
    setListState({ loading: true, error: "" });
    try {
      const data = await listQrDesigns();
      setDesigns(data.designs || []);
      setListState({ loading: false, error: "" });
    } catch (error) {
      setListState({ loading: false, error: error.message });
    }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  // ── Editing with Undo / Redo (a burst of changes = one step) ──
  const change = (patch) => {
    setDraft((current) => {
      const now = Date.now();
      if (now - lastPushRef.current > 600) {
        setHistory((h) => ({ past: [...h.past.slice(-HISTORY_LIMIT + 1), current], future: [] }));
      }
      lastPushRef.current = now;
      return { ...current, ...patch };
    });
  };
  const changeSettings = (patch) => change({ settings: { ...draft.settings, ...patch } });
  const undo = () => {
    if (!history.past.length) return;
    const previous = history.past[history.past.length - 1];
    setHistory((h) => ({ past: h.past.slice(0, -1), future: [draft, ...h.future] }));
    lastPushRef.current = 0;
    setDraft(previous);
  };
  const redo = () => {
    if (!history.future.length) return;
    const next = history.future[0];
    setHistory((h) => ({ past: [...h.past, draft], future: h.future.slice(1) }));
    lastPushRef.current = 0;
    setDraft(next);
  };

  const designForRender = useMemo(
    () => ({ link: draft.link, mode: draft.mode, settings, backgroundImg: background.img, photoImg: photo.img, logoImg: logo.img }),
    [draft.link, draft.mode, settings, background.img, photo.img, logo.img]
  );

  // ── Live preview + automatic verification (decoded back from the pixels) ──
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    if (!isHttpUrl(draft.link)) {
      setVerification({ ok: false, decoded: null, error: "INVALID_LINK" });
      canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
      return undefined;
    }
    const timer = setTimeout(() => {
      try {
        renderDesign(canvas, designForRender, PREVIEW_PX);
        setVerification(verifyCanvas(canvas, draft.link));
      } catch (error) {
        setVerification({ ok: false, decoded: null, error: "RENDER_FAILED" });
      }
    }, 120);
    return () => clearTimeout(timer);
  }, [designForRender, draft.link]);

  const missingImage = (draft.mode === "artistic" && !photo.img) || (draft.mode === "logo" && !logo.img);
  const imageError = photo.error || logo.error;

  const flash = (type, text) => setMessage({ type, text });

  const copyLink = async (link) => {
    try {
      await navigator.clipboard.writeText(link);
      flash("success", t("qrd_linkCopied", "Lien copie."));
    } catch {
      flash("error", t("qrd_copyFailed", "Impossible de copier le lien."));
    }
  };

  const payload = (name) => ({
    name: name || draft.name || "QR code",
    mode: draft.mode,
    link: draft.link,
    promoId: draft.promoId,
    settings,
    background: draft.background || {},
    photo: draft.mode === "artistic" ? draft.photo : {},
    logo: draft.mode === "logo" ? draft.logo : {},
    verified: verification.ok,
  });

  const renderPreviewBlob = async () => {
    const canvas = document.createElement("canvas");
    renderDesign(canvas, designForRender, 1024);
    const check = verifyCanvas(canvas, draft.link);
    if (!check.ok) throw new Error(t("qrd_unreadable", "Ce QR code n'est pas lisible : augmentez le contraste ou la taille des points."));
    return canvasBlob(canvas);
  };

  const save = async (asNew = false) => {
    if (!verification.ok || missingImage) {
      flash("error", t("qrd_cannotSave", "Enregistrement impossible : le QR code doit etre lisible et contenir exactement le lien."));
      return;
    }
    let name = draft.name;
    if (asNew || !draft.id) {
      const asked = window.prompt(t("qrd_askName", "Nom du QR code :"), asNew && draft.name ? `${draft.name} (2)` : draft.name || "");
      if (asked === null) return;
      name = asked.trim() || "QR code";
    }
    setBusy("save");
    try {
      const blob = await renderPreviewBlob();
      const result = !asNew && draft.id
        ? await updateQrDesign(draft.id, payload(name), blob)
        : await createQrDesign(payload(name), blob);
      setDraft((current) => ({ ...current, id: result.design._id, name: result.design.name }));
      flash("success", t("qrd_saved", "QR code enregistre."));
      await refresh();
    } catch (error) {
      flash("error", error.message);
    } finally {
      setBusy("");
    }
  };

  const edit = (design) => {
    setHistory({ past: [], future: [] });
    setDraft({
      id: design._id,
      name: design.name || "",
      mode: QR_MODES.includes(design.mode) ? design.mode : "classic",
      link: design.link || "",
      promoId: design.promoId || null,
      settings: { ...DEFAULT_SETTINGS, ...(design.settings || {}) },
      background: design.background || {},
      photo: design.photo || {},
      logo: design.logo || {},
    });
    flash("", "");
    window.scrollTo?.({ top: 0, behavior: "smooth" });
  };

  const duplicate = async (design) => {
    setBusy(`dup-${design._id}`);
    try {
      await duplicateQrDesign(design._id);
      await refresh();
      flash("success", t("qrd_duplicated", "QR code duplique."));
    } catch (error) {
      flash("error", error.message);
    } finally {
      setBusy("");
    }
  };

  const activate = async (design) => {
    if (design.active) return;
    if (!window.confirm(t("qrd_confirmActivate", "Activer \"{value0}\" ? Le QR code actuellement actif restera enregistre.", { value0: design.name }))) return;
    setBusy(`activate-${design._id}`);
    try {
      const response = await activateQrDesign(design._id);
      const activeId = String(response.activeDesignId || design._id);
      setDesigns((current) => current.map((item) => ({ ...item, active: String(item._id) === activeId })));
      flash("success", t("qrd_activated", "QR code active."));
      await refresh();
    } catch (error) {
      flash("error", error.message);
    } finally {
      setBusy("");
    }
  };

  const remove = async (design) => {
    if (!window.confirm(t("qrd_confirmDelete", "Supprimer definitivement \"{value0}\" ? Les posters deja telecharges ne changent pas.", { value0: design.name }))) return;
    setBusy(`del-${design._id}`);
    try {
      await deleteQrDesign(design._id);
      if (draft.id === design._id) setDraft((current) => ({ ...current, id: null }));
      await refresh();
      flash("success", t("qrd_deleted", "QR code supprime."));
    } catch (error) {
      flash("error", error.message);
    } finally {
      setBusy("");
    }
  };

  // Export of the design being edited, or of a saved one (its images are loaded first).
  const runExport = async (design, renderDesignInput, name) => {
    setBusy("export");
    try {
      const { blob, filename, verification: check } = await exportDesign(renderDesignInput, { format: exportFormat, size: exportSize, name });
      if (!check.ok) {
        flash("error", t("qrd_exportUnreadable", "Le fichier exporte n'a pas pu etre relu avec le bon lien : telechargement annule."));
        return;
      }
      downloadBlob(blob, filename);
      flash("success", t("qrd_exported", "{value0} telecharge et verifie (lien relu dans le fichier).", { value0: filename }));
    } catch (error) {
      flash("error", error.message === "IMAGE_LOAD_FAILED" ? t("qrd_imageBlocked", "Image inaccessible (le site qui l'heberge bloque son utilisation).") : error.message);
    } finally {
      setBusy("");
    }
  };

  const exportCurrent = () => runExport(draft, designForRender, draft.name || "qr-code");

  const exportSaved = async (design) => {
    try {
      const [backgroundImg, photoImg, logoImg] = await Promise.all([
        design.background?.url ? loadImage(design.background.url) : null,
        design.mode === "artistic" ? loadImage(design.photo?.url) : null,
        design.mode === "logo" ? loadImage(design.logo?.url) : null,
      ]);
      await runExport(design, { link: design.link, mode: design.mode, settings: normalizeSettings(design.settings), backgroundImg, photoImg, logoImg }, design.name);
    } catch {
      flash("error", t("qrd_imageBlocked", "Image inaccessible (le site qui l'heberge bloque son utilisation)."));
    }
  };

  // Makes a saved QR available to Page Builder & co. through the shared library.
  const addToLibrary = async (design) => {
    setBusy(`lib-${design._id}`);
    try {
      const response = await createMediaAsset({
        kind: "image",
        url: qrDesignImageUrl(design).split("?")[0],
        name: `QR - ${design.name}`.slice(0, 150),
        source: "editor",
        description: design.link,
        ctaUrl: design.link,
        shared: false,
      });
      flash("success", response.duplicate ? t("qrd_alreadyInLibrary", "Deja dans la bibliotheque.") : t("qrd_addedToLibrary", "Ajoute a la Media Library (utilisable dans Page Builder)."));
    } catch (error) {
      flash("error", error.message);
    } finally {
      setBusy("");
    }
  };

  const pickImage = (media) => {
    const target = pickerFor;
    if (!target || !media?.url) return;
    const source = media.filename || media.originalName ? "upload" : media.assetId ? "library" : "url";
    if (source === "url" && !isSafeRemoteImageUrl(media.url)) {
      flash("error", t("qrd_unsafeImageUrl", "Cette URL d'image locale ou privee n'est pas autorisee."));
      return;
    }
    change({ [target]: { url: media.url, source, ...(media.assetId ? { mediaAssetId: media.assetId } : {}) } });
    setPickerFor(null);
  };

  const applyProfilePreset = (preset) => {
    const profileSettings = {
      qrScale: preset === "whatsapp" ? 0.7 : 0.74,
      qrX: 0,
      qrY: 0,
      canvasTransparent: false,
      canvasColor: "#ffffff",
      lightTransparent: false,
      lightColor: "#ffffff",
      ecLevel: "H",
    };
    changeSettings(profileSettings);
    setExportSize(preset === "whatsapp" ? 640 : 1080);
    flash("success", preset === "whatsapp"
      ? t("qrd_whatsappPresetApplied", "Preset WhatsApp applique : zone sure centree, 640 x 640.")
      : t("qrd_facebookPresetApplied", "Preset Facebook applique : zone sure centree, 1080 x 1080."));
  };

  const activeImageKey = draft.mode === "artistic" ? "photo" : "logo";
  const activeImage = draft[activeImageKey] || {};
  const removeBackground = async () => {
    const loaded = activeImageKey === "photo" ? photo.img : logo.img;
    if (!loaded || busy) return;
    setBusy("remove-background");
    try {
      const blob = await removeImageBackground(loaded);
      const file = typeof File === "function"
        ? new File([blob], `qr-${activeImageKey}-transparent.png`, { type: "image/png" })
        : blob;
      const uploaded = await uploadMediaFile(file);
      const imageWithoutAssetId = { ...activeImage };
      delete imageWithoutAssetId.mediaAssetId;
      change({
        [activeImageKey]: {
          ...imageWithoutAssetId,
          url: uploaded.url,
          source: "upload",
          backgroundRemoved: true,
          originalUrl: activeImage.originalUrl || activeImage.url,
          originalSource: activeImage.originalSource || activeImage.source || "url",
          ...(activeImage.originalMediaAssetId || activeImage.mediaAssetId
            ? { originalMediaAssetId: activeImage.originalMediaAssetId || activeImage.mediaAssetId }
            : {}),
        },
      });
      flash("success", t("qrd_backgroundRemoved", "Fond supprime. L'image transparente est prete."));
    } catch (error) {
      flash("error", error?.name === "SecurityError"
        ? t("qrd_backgroundCors", "Cette image distante bloque le traitement. Importez le fichier depuis votre appareil.")
        : t("qrd_backgroundFailed", "Impossible de supprimer le fond de cette image."));
    } finally {
      setBusy("");
    }
  };

  const restoreOriginalImage = () => {
    if (!activeImage.originalUrl) return;
    change({
      [activeImageKey]: {
        url: activeImage.originalUrl,
        source: activeImage.originalSource || "url",
        ...(activeImage.originalMediaAssetId ? { mediaAssetId: activeImage.originalMediaAssetId } : {}),
      },
    });
    flash("success", t("qrd_originalRestored", "Image originale restauree."));
  };

  const status = (() => {
    if (verification.error === "INVALID_LINK") return { cls: "warn", text: t("qrd_linkInvalid", "Saisissez un lien http(s) valide.") };
    if (missingImage) return { cls: "warn", text: draft.mode === "artistic" ? t("qrd_needPhoto", "Choisissez une photo pour le mode artistique.") : t("qrd_needLogo", "Choisissez un logo.") };
    if (verification.ok) return { cls: "ok", text: t("qrd_verified", "Lisible : contient exactement {value0}", { value0: draft.link }) };
    if (verification.error === "TAINTED") return { cls: "error", text: t("qrd_imageBlocked", "Image inaccessible (le site qui l'heberge bloque son utilisation).") };
    if (verification.error === "MISMATCH") return { cls: "error", text: t("qrd_mismatch", "Lien relu different : {value0}", { value0: verification.decoded }) };
    return { cls: "error", text: t("qrd_unreadable", "Ce QR code n'est pas lisible : augmentez le contraste ou la taille des points.") };
  })();

  return (
    <div className="qrd-root">
      <div className="qrd-layout">
        {/* ── Editor ── */}
        <section className="qrd-panel qrd-editor" aria-label={t("qrd_editor", "Editeur")}>
          <div className="qrd-toolbar">
            <strong>{draft.id ? t("qrd_editing", "Modification : {value0}", { value0: draft.name }) : t("qrd_new", "Nouveau QR code")}</strong>
            <span className="qrd-toolbar-actions">
              <button type="button" onClick={undo} disabled={!history.past.length} title={t("qrd_undo", "Annuler")}>↶</button>
              <button type="button" onClick={redo} disabled={!history.future.length} title={t("qrd_redo", "Retablir")}>↷</button>
              <button type="button" onClick={() => { setDraft(emptyDraft(promoLinks[0]?.value || "")); setHistory({ past: [], future: [] }); }}>{t("qrd_newButton", "Nouveau")}</button>
            </span>
          </div>

          <div className="qrd-modes" role="radiogroup" aria-label={t("qrd_mode", "Type de QR code")}>
            {QR_MODES.map((mode) => (
              <button key={mode} type="button" role="radio" aria-checked={draft.mode === mode} className={draft.mode === mode ? "active" : ""} onClick={() => change({ mode })}>
                {MODE_LABELS[mode]}
              </button>
            ))}
          </div>

          <div className="qrd-group">
            <div className="qrd-group-title">{t("qrd_destination", "Lien de destination")}</div>
            {promoLinks.length > 0 && (
              <label className="qrd-field">
                <span>{t("qrd_fromPromo", "Depuis une promotion")}</span>
                <select
                  value={promoLinks.find((p) => p.value === draft.link)?.id || ""}
                  onChange={(e) => {
                    const promo = promoLinks.find((p) => String(p.id) === e.target.value);
                    if (promo) change({ link: promo.value, promoId: /^[0-9a-f]{24}$/i.test(String(promo.id)) ? String(promo.id) : null });
                  }}
                >
                  <option value="">{t("qrd_customLink", "Lien personnalise")}</option>
                  {promoLinks.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
                </select>
              </label>
            )}
            <label className="qrd-field">
              <span>URL</span>
              <input type="url" value={draft.link} onChange={(e) => change({ link: e.target.value.trim(), promoId: null })} placeholder="https://elakay.com/..." />
            </label>
            {draft.link.length > 120 && <p className="qrd-density-warning">{t("qrd_longUrlWarning", "Lien long : le QR sera plus dense. Utilisez si possible un lien court de redirection Elakay, surtout pour une photo de profil.")}</p>}
            <div className="qrd-presets" aria-label={t("qrd_profilePresets", "Prereglages photo de profil")}>
              <span>{t("qrd_profilePresets", "Photo de profil")}</span>
              <button type="button" onClick={() => applyProfilePreset("facebook")}>Facebook</button>
              <button type="button" onClick={() => applyProfilePreset("whatsapp")}>WhatsApp</button>
            </div>
          </div>

          <div className="qrd-layer-panel">
            <div className="qrd-layer-heading"><span>1</span><strong>{t("qrd_generalBackground", "Fond general")}</strong></div>
            <div className="qrd-row">
              <ColorField label={t("qrd_backgroundColor", "Couleur du fond general")} value={settings.canvasColor} onChange={(canvasColor) => changeSettings({ canvasColor })} />
              <label className="qrd-check"><input type="checkbox" checked={settings.canvasTransparent} onChange={(e) => changeSettings({ canvasTransparent: e.target.checked })} /> {t("qrd_generalTransparent", "Fond general transparent")}</label>
            </div>
            <div className="qrd-image-row">
              {draft.background?.url ? <img src={toImageUrl(draft.background.url)} alt="" className="qrd-thumb" /> : <span className="qrd-thumb qrd-thumb--empty">—</span>}
              <button type="button" onClick={() => setPickerFor(pickerFor === "background" ? null : "background")}>{t("qrd_backgroundImage", "Choisir une image de fond")}</button>
              {draft.background?.url && <button type="button" onClick={() => change({ background: {} })}>{t("qrd_removeImage", "Retirer")}</button>}
            </div>
            {pickerFor === "background" && <UniversalMediaPicker kind="image" onUpload={uploadMediaFile} onSelect={pickImage} value={draft.background?.url || ""} />}
            {draft.background?.url && <>
              <Slider label={t("qrd_backgroundScale", "Dimensions de l'image de fond")} value={settings.backgroundScale} min={0.5} max={3} step={0.05} onChange={(backgroundScale) => changeSettings({ backgroundScale })} />
              <div className="qrd-row">
                <Slider label={t("qrd_backgroundX", "Position horizontale")} value={settings.backgroundX} min={-50} max={50} onChange={(backgroundX) => changeSettings({ backgroundX })} suffix="%" />
                <Slider label={t("qrd_backgroundY", "Position verticale")} value={settings.backgroundY} min={-50} max={50} onChange={(backgroundY) => changeSettings({ backgroundY })} suffix="%" />
              </div>
              <Slider label={t("qrd_backgroundOpacity", "Opacite de l'image de fond")} value={settings.backgroundOpacity} min={0.05} max={1} step={0.05} onChange={(backgroundOpacity) => changeSettings({ backgroundOpacity })} />
              <button type="button" onClick={() => changeSettings({ backgroundX: 0, backgroundY: 0 })}>{t("qrd_centerBackground", "Centrer l'image de fond")}</button>
            </>}
          </div>

          <div className="qrd-layer-panel">
            <div className="qrd-layer-heading"><span>2</span><strong>{t("qrd_qrLayer", "QR code")}</strong></div>
            <div className="qrd-size-controls">
              <button type="button" aria-label={t("qrd_reduceQr", "Reduire le QR code")} onClick={() => changeSettings({ qrScale: Math.max(0.45, settings.qrScale - 0.05) })}>−</button>
              <Slider label={t("qrd_qrSize", "Taille du QR code")} value={settings.qrScale} min={0.45} max={1} step={0.01} onChange={(qrScale) => changeSettings({ qrScale })} />
              <button type="button" aria-label={t("qrd_enlargeQr", "Agrandir le QR code")} onClick={() => changeSettings({ qrScale: Math.min(1, settings.qrScale + 0.05) })}>+</button>
            </div>
            <div className="qrd-row">
              <Slider label={t("qrd_qrX", "Position horizontale")} value={settings.qrX} min={-50} max={50} onChange={(qrX) => changeSettings({ qrX })} suffix="%" />
              <Slider label={t("qrd_qrY", "Position verticale")} value={settings.qrY} min={-50} max={50} onChange={(qrY) => changeSettings({ qrY })} suffix="%" />
            </div>
            <button type="button" onClick={() => changeSettings({ qrX: 0, qrY: 0 })}>{t("qrd_centerQr", "Centrer automatiquement le QR")}</button>
            <div className="qrd-row">
              <ColorField label={t("qrd_dark", "Couleur des modules")} value={settings.darkColor} onChange={(darkColor) => changeSettings({ darkColor })} />
              <ColorField label={t("qrd_qrBackground", "Arriere-plan du QR")} value={settings.lightColor} onChange={(lightColor) => changeSettings({ lightColor })} />
            </div>
            <div className="qrd-row">
              <label className="qrd-check"><input type="checkbox" checked={settings.lightTransparent} onChange={(e) => changeSettings({ lightTransparent: e.target.checked })} /> {t("qrd_qrTransparent", "Arriere-plan du QR transparent")}</label>
              {draft.mode !== "artistic" && <label className="qrd-field"><span>{t("qrd_moduleStyle", "Forme des modules")}</span><select value={settings.moduleStyle} onChange={(e) => changeSettings({ moduleStyle: e.target.value })}><option value="square">{t("qrd_square", "Carres")}</option><option value="rounded">{t("qrd_rounded", "Arrondis")}</option><option value="dot">{t("qrd_dots", "Ronds")}</option></select></label>}
            </div>
            {draft.mode === "artistic" && <>
              <Slider label={t("qrd_contrast", "Contraste du code")} value={settings.contrast} min={0.2} max={0.95} step={0.05} onChange={(contrast) => changeSettings({ contrast })} />
              <Slider label={t("qrd_coreSize", "Taille des points")} value={settings.coreSize} min={0.25} max={0.75} step={0.05} onChange={(coreSize) => changeSettings({ coreSize })} />
            </>}
            <label className="qrd-field"><span>{t("qrd_ecLevel", "Correction d'erreurs")}</span><select value={settings.ecLevel} onChange={(e) => changeSettings({ ecLevel: e.target.value })}><option value="auto">{t("qrd_ecAuto", "Automatique (H avec image)")}</option><option value="M">M (15 %)</option><option value="Q">Q (25 %)</option><option value="H">H (30 %)</option></select></label>
          </div>

          <div className="qrd-layer-panel">
            <div className="qrd-layer-heading"><span>3</span><strong>{draft.mode === "artistic" ? t("qrd_photo", "Image artistique") : t("qrd_logo", "Logo / Image")}</strong></div>
            {draft.mode === "classic" ? <p className="qrd-msg">{t("qrd_classicNoImage", "Le mode classique ne superpose aucun logo. Choisissez Logo QR Code ou Artistic QR Code pour activer cette couche.")}</p> : <>
              <div className="qrd-image-row">
                {(draft.mode === "artistic" ? draft.photo?.url : draft.logo?.url)
                  ? <img src={toImageUrl(draft.mode === "artistic" ? draft.photo.url : draft.logo.url)} alt="" className="qrd-thumb" />
                  : <span className="qrd-thumb qrd-thumb--empty">—</span>}
                <button type="button" onClick={() => setPickerFor(pickerFor ? null : draft.mode === "artistic" ? "photo" : "logo")}>
                  {t("qrd_chooseImage", "Choisir une image")}
                </button>
                {(draft.mode === "artistic" ? draft.photo?.url : draft.logo?.url) && (
                  <button type="button" onClick={() => change({ [draft.mode === "artistic" ? "photo" : "logo"]: {} })}>{t("qrd_removeImage", "Retirer")}</button>
                )}
              </div>
              {activeImage.url && (
                <div className="qrd-background-actions">
                  <button type="button" className="qrd-remove-background" onClick={removeBackground} disabled={busy !== "" || !((activeImageKey === "photo" ? photo.img : logo.img))}>
                    {busy === "remove-background" ? t("qrd_removingBackground", "Suppression du fond…") : t("qrd_removeBackground", "Supprimer le fond")}
                  </button>
                  {activeImage.backgroundRemoved && activeImage.originalUrl && (
                    <button type="button" onClick={restoreOriginalImage} disabled={busy !== ""}>{t("qrd_restoreOriginal", "Restaurer l'image originale")}</button>
                  )}
                  {activeImage.backgroundRemoved && <span className="qrd-transparency-badge">{t("qrd_transparentPng", "PNG transparent")}</span>}
                </div>
              )}
              {(imageError || background.error) && <p className="qrd-msg error">{t("qrd_imageBlocked", "Image inaccessible (le site qui l'heberge bloque son utilisation).")}</p>}
              {(pickerFor === "photo" || pickerFor === "logo") && (
                <UniversalMediaPicker kind="image" onUpload={uploadMediaFile} onSelect={pickImage} value={(pickerFor === "photo" ? draft.photo?.url : draft.logo?.url) || ""} />
              )}
              {draft.mode === "artistic" ? (
                <>
                  <Slider label={t("qrd_photoScale", "Zoom de la photo")} value={settings.photoScale} min={0.5} max={3} step={0.05} onChange={(photoScale) => changeSettings({ photoScale })} />
                  <Slider label={t("qrd_photoX", "Position horizontale")} value={settings.photoX} min={-50} max={50} onChange={(photoX) => changeSettings({ photoX })} suffix="%" />
                  <Slider label={t("qrd_photoY", "Position verticale")} value={settings.photoY} min={-50} max={50} onChange={(photoY) => changeSettings({ photoY })} suffix="%" />
                  <button type="button" onClick={() => changeSettings({ photoX: 0, photoY: 0 })}>{t("qrd_centerImage", "Centrer l'image")}</button>
                  <Slider label={t("qrd_opacity", "Opacite de la photo")} value={settings.photoOpacity} min={0.1} max={1} step={0.05} onChange={(photoOpacity) => changeSettings({ photoOpacity })} />
                  <Slider label={t("qrd_brightness", "Luminosite de la photo")} value={settings.brightness} min={0.5} max={1.5} step={0.05} onChange={(brightness) => changeSettings({ brightness })} />
                  <Slider label={t("qrd_contrast", "Contraste du code")} value={settings.contrast} min={0.2} max={0.95} step={0.05} onChange={(contrast) => changeSettings({ contrast })} />
                  <Slider label={t("qrd_coreSize", "Taille des points")} value={settings.coreSize} min={0.25} max={0.75} step={0.05} onChange={(coreSize) => changeSettings({ coreSize })} />
                </>
              ) : (
                <>
                  <Slider label={t("qrd_logoSize", "Taille du logo")} value={settings.logoSize} min={10} max={28} onChange={(logoSize) => changeSettings({ logoSize })} suffix="%" />
                  <div className="qrd-row"><Slider label={t("qrd_logoX", "Position horizontale")} value={settings.logoX} min={-50} max={50} onChange={(logoX) => changeSettings({ logoX })} suffix="%" /><Slider label={t("qrd_logoY", "Position verticale")} value={settings.logoY} min={-50} max={50} onChange={(logoY) => changeSettings({ logoY })} suffix="%" /></div>
                  <button type="button" onClick={() => changeSettings({ logoX: 0, logoY: 0 })}>{t("qrd_centerLogo", "Centrer le logo")}</button>
                  <label className="qrd-check"><input type="checkbox" checked={settings.logoPlate} onChange={(e) => changeSettings({ logoPlate: e.target.checked })} /> {t("qrd_logoPlate", "Fond derriere le logo")}</label>
                  {settings.logoPlate && <div className="qrd-row"><ColorField label={t("qrd_logoBackgroundColor", "Couleur du fond du logo")} value={settings.logoPlateColor} onChange={(logoPlateColor) => changeSettings({ logoPlateColor })} /><label className="qrd-check"><input type="checkbox" checked={settings.logoPlateTransparent} onChange={(e) => changeSettings({ logoPlateTransparent: e.target.checked })} /> {t("qrd_logoBackgroundTransparent", "Fond du logo transparent")}</label></div>}
                </>
              )}
            </>}
          </div>

          <div className="qrd-group">
            <div className="qrd-group-title">{t("qrd_text", "Texte personnalise")}</div>
            <label className="qrd-field">
              <span>{t("qrd_textContent", "Texte")}</span>
              <input type="text" maxLength={80} value={settings.text} onChange={(e) => changeSettings({ text: e.target.value })} placeholder={t("qrd_textPlaceholder", "Scannez-moi")} />
            </label>
            {settings.text && (
              <>
                <div className="qrd-row">
                  <ColorField label={t("qrd_textColor", "Couleur du texte")} value={settings.textColor} onChange={(textColor) => changeSettings({ textColor })} />
                  <label className="qrd-field">
                    <span>{t("qrd_textPosition", "Position")}</span>
                    <select value={settings.textPosition} onChange={(e) => changeSettings({ textPosition: e.target.value })}>
                      <option value="bottom">{t("qrd_bottom", "En bas")}</option>
                      <option value="top">{t("qrd_top", "En haut")}</option>
                    </select>
                  </label>
                </div>
                <Slider label={t("qrd_textSize", "Taille du texte")} value={settings.textSize} min={4} max={14} onChange={(textSize) => changeSettings({ textSize })} suffix="%" />
                <label className="qrd-check"><input type="checkbox" checked={settings.textBold} onChange={(e) => changeSettings({ textBold: e.target.checked })} /> {t("qrd_bold", "Gras")}</label>
              </>
            )}
          </div>
        </section>

        {/* ── Preview / save / export ── */}
        <section className="qrd-panel qrd-preview" aria-label={t("qrd_preview", "Previsualisation")}>
          <div className="qrd-canvas-wrap">
            <canvas ref={canvasRef} width={PREVIEW_PX} height={PREVIEW_PX} className="qrd-canvas" aria-label={t("qrd_preview", "Previsualisation")} />
          </div>
          <p className={`qrd-status ${status.cls}`} role="status" data-verified={verification.ok && !missingImage ? "true" : "false"}>{status.text}</p>
          <div className="qrd-actions">
            <button type="button" className="primary" onClick={() => save(false)} disabled={busy !== "" || !verification.ok || missingImage}>
              {busy === "save" ? "…" : t("qrd_save", "Enregistrer")}
            </button>
            <button type="button" onClick={() => save(true)} disabled={busy !== "" || !verification.ok || missingImage}>{t("qrd_saveAs", "Enregistrer sous")}</button>
          </div>
          <div className="qrd-export">
            <div className="qrd-group-title">{t("qrd_download", "Telecharger")}</div>
            <div className="qrd-row">
              <select value={exportFormat} onChange={(e) => setExportFormat(e.target.value)} aria-label={t("qrd_format", "Format")}>
                {EXPORT_FORMATS.map((format) => <option key={format} value={format}>{format.toUpperCase()}</option>)}
              </select>
              <select value={exportSize} onChange={(e) => setExportSize(Number(e.target.value))} aria-label={t("qrd_resolution", "Resolution")}>
                {EXPORT_SIZES.map((size) => <option key={size} value={size}>{size} × {size}</option>)}
              </select>
              <button type="button" onClick={exportCurrent} disabled={busy !== "" || !verification.ok || missingImage}>
                {busy === "export" ? "…" : t("qrd_downloadButton", "Telecharger")}
              </button>
            </div>
          </div>
          {message.text && <p className={`qrd-msg ${message.type}`} role="alert">{message.text}</p>}
        </section>
      </div>

      {/* ── Saved designs ── */}
      <section className="qrd-panel qrd-saved" aria-label={t("qrd_savedTitle", "Mes QR codes")}>
        <div className="qrd-group-title">{t("qrd_savedTitle", "Mes QR codes")} {designs.length > 0 && <span className="qrd-count">{designs.length}</span>}</div>
        <div className="qrd-list-tools">
          <label className="qrd-field">
            <span>{t("qrd_search", "Rechercher")}</span>
            <input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder={t("qrd_searchPlaceholder", "Nom ou destination…")} />
          </label>
          <label className="qrd-field">
            <span>{t("qrd_filterMode", "Type")}</span>
            <select value={modeFilter} onChange={(event) => setModeFilter(event.target.value)}>
              <option value="all">{t("qrd_allModes", "Tous")}</option>
              {QR_MODES.map((mode) => <option key={mode} value={mode}>{MODE_LABELS[mode]}</option>)}
            </select>
          </label>
        </div>
        {listState.loading && <p className="qrd-msg">{t("qrd_loading", "Chargement…")}</p>}
        {listState.error && <p className="qrd-msg error">{listState.error}</p>}
        {!listState.loading && !designs.length && !listState.error && <p className="qrd-msg">{t("qrd_none", "Aucun QR code enregistre.")}</p>}
        {!listState.loading && designs.length > 0 && !visibleDesigns.length && <p className="qrd-msg">{t("qrd_noMatch", "Aucun QR code ne correspond aux filtres.")}</p>}
        <div className="qrd-grid">
          {visibleDesigns.map((design) => (
            <article key={design._id} className={`qrd-card${draft.id === design._id ? " is-editing" : ""}${design.active ? " is-active" : ""}`} data-design-id={design._id} data-active={design.active ? "true" : "false"}>
              <button type="button" className="qrd-card-image" onClick={() => setPreviewDesign(design)} title={t("qrd_previewAction", "Previsualiser")}>
                <img src={qrDesignImageUrl(design)} alt={design.name} loading="lazy" />
                <span className="qrd-card-preview-cue" aria-hidden="true"><span>⌕</span>{t("qrd_previewAction", "Previsualiser")}</span>
              </button>
              <div className="qrd-card-content">
                <div className="qrd-card-heading">
                  <strong title={design.name}>{design.name}</strong>
                  <span className="qrd-card-heading-badges">
                    {design.active && <span className="qrd-card-active-badge">{t("qrd_active", "Actif")}</span>}
                    <span className="qrd-card-meta">{MODE_LABELS[design.mode] || design.mode}</span>
                  </span>
                </div>
                <button type="button" className="qrd-card-destination" onClick={() => copyLink(design.link)} title={t("qrd_copyLink", "Copier le lien")} aria-label={t("qrd_copyLink", "Copier le lien")}>
                  <span aria-hidden="true">↗</span><span>{design.link}</span><em>{t("qrd_copyShort", "Copier")}</em>
                </button>
                <div className="qrd-card-primary-actions">
                  <button type="button" className="qrd-card-btn qrd-card-btn--activate" onClick={() => activate(design)} disabled={busy !== "" || design.active} aria-pressed={design.active}>
                    <span aria-hidden="true">✓</span>{design.active ? t("qrd_active", "Actif") : busy === `activate-${design._id}` ? "…" : t("qrd_activate", "Activer")}
                  </button>
                  <button type="button" className="qrd-card-btn qrd-card-btn--primary" onClick={() => edit(design)}><span aria-hidden="true">✎</span>{t("qrd_edit", "Modifier")}</button>
                  <button type="button" className="qrd-card-btn qrd-card-btn--download" onClick={() => exportSaved(design)} disabled={busy !== ""}><span aria-hidden="true">↓</span>{t("qrd_downloadShort", "Telecharger")}</button>
                </div>
                <div className="qrd-card-secondary-actions">
                  <a className="qrd-card-icon-action" href={design.link} target="_blank" rel="noopener noreferrer" title={t("qrd_openLink", "Ouvrir")} aria-label={t("qrd_openLink", "Ouvrir")}><span aria-hidden="true">↗</span></a>
                  <button type="button" className="qrd-card-icon-action" onClick={() => duplicate(design)} disabled={busy !== ""} title={t("qrd_duplicate", "Dupliquer")} aria-label={t("qrd_duplicate", "Dupliquer")}><span aria-hidden="true">⧉</span></button>
                  <button type="button" className="qrd-card-library-action" onClick={() => addToLibrary(design)} disabled={busy !== ""}><span aria-hidden="true">▣</span>{t("qrd_toLibrary", "Media Library")}</button>
                  <button type="button" className="qrd-card-icon-action qrd-card-icon-action--danger" onClick={() => remove(design)} disabled={busy !== ""} title={t("qrd_delete", "Supprimer")} aria-label={t("qrd_delete", "Supprimer")}><span aria-hidden="true">⌫</span></button>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {previewDesign && createPortal((
        <div className="qrd-modal" role="dialog" aria-modal="true" aria-labelledby="qrd-preview-title" onClick={() => setPreviewDesign(null)}>
          <div className="qrd-modal-box" onClick={(e) => e.stopPropagation()}>
            <header className="qrd-modal-header">
              <div>
                <span>{t("qrd_previewAction", "Previsualisation")}</span>
                <strong id="qrd-preview-title">{previewDesign.name}</strong>
              </div>
              <button type="button" className="qrd-modal-close" onClick={() => setPreviewDesign(null)} aria-label={t("qrd_close", "Fermer")}>×</button>
            </header>
            <div className="qrd-modal-image-wrap">
              <img src={qrDesignImageUrl(previewDesign)} alt={previewDesign.name} />
            </div>
            <div className="qrd-modal-destination">
              <span>{t("qrd_destination", "Lien de destination")}</span>
              <code title={previewDesign.link}>{previewDesign.link}</code>
            </div>
            <div className="qrd-actions qrd-modal-actions">
              <button type="button" className="qrd-modal-action qrd-modal-action--primary" onClick={() => copyLink(previewDesign.link)}><span aria-hidden="true">⧉</span>{t("qrd_copyLink", "Copier le lien")}</button>
              <a className="qrd-modal-action qrd-modal-action--secondary" href={previewDesign.link} target="_blank" rel="noopener noreferrer"><span aria-hidden="true">↗</span>{t("qrd_openLink", "Ouvrir")}</a>
            </div>
          </div>
        </div>
      ), document.body)}
    </div>
  );
}
